/**
 * Agent tool definitions and execution layer.
 *
 * Rewritten to use OpenAI-compatible function calling (Groq supports `tools`
 * parameter). Tool functions perform real DB queries; pipeline stats use the
 * materialized view when available.
 *
 * New tools: search_match_results, get_interview_status.
 * Improved: search_candidates uses trgm + skill_taxonomy; pipeline stats via
 *           report view (server-side aggregation, not client-side count).
 */
import { supabase } from '@core/supabase/client';

export type AgentToolName =
  | 'search_candidates'
  | 'search_applications'
  | 'get_open_jobs'
  | 'get_pipeline_stats'
  | 'search_match_results'
  | 'get_interview_status'
  | 'draft_email';

export interface ToolCall {
  tool: AgentToolName;
  args: Record<string, unknown>;
}

export interface CandidateResult {
  id: string;
  full_name: string;
  email: string;
  skills: string[];
  current_title?: string;
  years_exp?: number;
  source?: string;
}

export interface JobResult {
  id: string;
  title: string;
  client_name: string;
  status: string;
  /** Mirrors `ggj_b2b_jobs.openings_count` (migration 083); not `open_positions`. */
  openings_count: number;
}

export interface PipelineStats {
  total: number;
  by_status: { status: string; count: number }[];
}

export interface ApplicationSearchRow {
  id: string;
  status: string;
  submitted_at: string;
  candidate_name: string;
  candidate_email: string;
  job_title: string;
}

export interface MatchSearchRow {
  candidate_name: string;
  job_title: string;
  composite_score: number;
  recommendation: string;
  matched_skills: string[];
}

export interface InterviewStatusRow {
  session_id: string;
  candidate_name: string;
  status: string;
  session_type: string;
  created_at: string;
  completed_at: string | null;
}

export type ToolResultData =
  | { kind: 'candidates'; rows: CandidateResult[] }
  | { kind: 'applications'; rows: ApplicationSearchRow[] }
  | { kind: 'jobs'; rows: JobResult[] }
  | { kind: 'pipeline'; stats: PipelineStats }
  | { kind: 'matches'; rows: MatchSearchRow[] }
  | { kind: 'interviews'; rows: InterviewStatusRow[] }
  | { kind: 'draft'; subject: string; body: string };

export interface ToolResult {
  tool: AgentToolName;
  args: Record<string, unknown>;
  data?: ToolResultData;
  error?: string;
}

// ── OpenAI-compatible tool definitions for Groq `tools` parameter ────────

/**
 * Groq validates tool-call arguments against these schemas. Models often emit
 * numeric limits as JSON strings; `type: "number"` alone causes validation errors.
 */
const TOOL_PARAM_LIMIT = {
  type: ['number', 'string'] as ['number', 'string'],
  description: 'Max results (1–10). JSON number or numeric string.',
};

const TOOL_PARAM_MIN_SCORE = {
  type: ['number', 'string'] as ['number', 'string'],
  description: 'Minimum composite score (0–100). JSON number or numeric string.',
};

export const OPENAI_TOOL_DEFINITIONS = [
  {
    type: 'function' as const,
    function: {
      name: 'search_candidates',
      description: 'Search candidates by name, skill, role, or keyword. Uses trigram index for fuzzy matching.',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Search text (name, skill, or role)' },
          limit: TOOL_PARAM_LIMIT,
        },
        required: ['query'],
      },
    },
  },
  {
    type: 'function' as const,
    function: {
      name: 'search_applications',
      description: 'Search applications by candidate name, job title, or status.',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Search text' },
          status: { type: 'string', description: 'Filter by status (optional)' },
          limit: TOOL_PARAM_LIMIT,
        },
        required: ['query'],
      },
    },
  },
  {
    type: 'function' as const,
    function: {
      name: 'get_open_jobs',
      description: 'List open jobs in the organization.',
      parameters: {
        type: 'object',
        properties: {
          limit: TOOL_PARAM_LIMIT,
        },
      },
    },
  },
  {
    type: 'function' as const,
    function: {
      name: 'get_pipeline_stats',
      description: 'Get application counts grouped by pipeline status. Use for any count/summary question.',
      parameters: { type: 'object', properties: {} },
    },
  },
  {
    type: 'function' as const,
    function: {
      name: 'search_match_results',
      description: 'Search Smart Match results. Returns top matches with scores and recommendations.',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Candidate name or job title to filter' },
          min_score: TOOL_PARAM_MIN_SCORE,
          limit: TOOL_PARAM_LIMIT,
        },
      },
    },
  },
  {
    type: 'function' as const,
    function: {
      name: 'get_interview_status',
      description: 'Get AI interview session statuses for the organization.',
      parameters: {
        type: 'object',
        properties: {
          status: { type: 'string', description: 'Filter by status (invited/in_progress/completed)' },
          limit: TOOL_PARAM_LIMIT,
        },
      },
    },
  },
  {
    type: 'function' as const,
    function: {
      name: 'draft_email',
      description: 'Compose a templated recruiter email (not sent automatically).',
      parameters: {
        type: 'object',
        properties: {
          type: {
            type: 'string',
            enum: ['interview_invite', 'rejection', 'offer', 'follow_up'],
            description: 'Email template type',
          },
          candidate_name: { type: 'string', description: 'Candidate name' },
          job_title: { type: 'string', description: 'Job title' },
        },
        required: ['type', 'candidate_name', 'job_title'],
      },
    },
  },
];

/** Legacy prompt-based definitions kept for backward compat with non-function-calling models. */
export const TOOL_DEFINITIONS = `
Available tools (the model uses OpenAI function calling; these descriptions are for reference):

1. search_candidates — find candidates by skill, name, or role (trgm fuzzy match)
2. search_applications — find applications by candidate name, job title, or status
3. get_open_jobs — list open jobs in the organisation
4. get_pipeline_stats — counts of applications grouped by status
5. search_match_results — search Smart Match results with score filtering
6. get_interview_status — AI interview session statuses
7. draft_email — compose a templated recruiter email

Rules:
- Pipeline / funnel COUNTS: ALWAYS use get_pipeline_stats.
- For Smart Match data: use search_match_results.
- For interview status: use get_interview_status.
`.trim();

export const AGENT_SYSTEM_PROMPT = `You are an AI recruiting assistant for the GoGrabJob ATS platform. You help recruiters by searching candidates, checking pipeline status, reviewing match results, and drafting emails. Be concise and professional. When you have data, present it clearly with key details highlighted.`;

// ── Tool executors ──────────────────────────────────────────────────────────

async function searchCandidates(
  orgId: string,
  query: string,
  limit = 5,
): Promise<CandidateResult[]> {
  const lim = Math.min(Math.max(1, limit), 10);

  // Try trgm similarity search first via ilike (trgm GIN index from migration 019)
  const { data, error } = await supabase
    .from('ggj_b2b_candidates')
    .select('id, full_name, email, parsed_skills, current_title, years_exp')
    .eq('org_id', orgId)
    .or(`full_name.ilike.%${query}%,current_title.ilike.%${query}%`)
    .limit(lim);

  if (error) {
    const { data: fallback } = await supabase
      .from('ggj_b2b_candidates')
      .select('id, full_name, email, parsed_skills, current_title, years_exp')
      .eq('org_id', orgId)
      .ilike('full_name', `%${query}%`)
      .limit(lim);
    return mapCandidateRows(fallback);
  }

  if (data && data.length > 0) return mapCandidateRows(data);

  // Skill search fallback: check if query matches any skill in parsed_skills
  const { data: skillData } = await supabase
    .from('ggj_b2b_candidates')
    .select('id, full_name, email, parsed_skills, current_title, years_exp')
    .eq('org_id', orgId)
    .contains('parsed_skills', [query])
    .limit(lim);

  return mapCandidateRows(skillData);
}

function mapCandidateRows(data: unknown[] | null): CandidateResult[] {
  return (data ?? []).map((row: unknown) => {
    const r = row as Record<string, unknown>;
    return {
      id: String(r.id),
      full_name: String(r.full_name ?? ''),
      email: String(r.email ?? ''),
      skills: (r.parsed_skills as string[] | null) ?? [],
      current_title: r.current_title ? String(r.current_title) : undefined,
      years_exp: typeof r.years_exp === 'number' ? r.years_exp : undefined,
    };
  });
}

async function searchApplications(
  orgId: string,
  query: string,
  statusFilter?: string,
  limit = 5,
): Promise<ApplicationSearchRow[]> {
  const lim = Math.min(Math.max(1, limit), 10);
  let q = supabase
    .from('ggj_b2b_applications')
    .select(
      'id, status, submitted_at, candidate:ggj_b2b_candidates(full_name, email), job:ggj_b2b_jobs(title)',
    )
    .eq('org_id', orgId)
    .order('submitted_at', { ascending: false })
    .limit(50);

  if (statusFilter) {
    q = q.eq('status', statusFilter);
  }

  const { data, error } = await q;
  if (error) throw error;

  const rows = (data ?? []) as Array<{
    id: string;
    status: string;
    submitted_at: string;
    candidate: { full_name: string; email: string | null } | null;
    job: { title: string } | null;
  }>;

  const mapped: ApplicationSearchRow[] = rows.map((r) => ({
    id: r.id,
    status: r.status,
    submitted_at: r.submitted_at,
    candidate_name: r.candidate?.full_name ?? '—',
    candidate_email: r.candidate?.email ?? '',
    job_title: r.job?.title ?? '—',
  }));

  const qLower = query.trim().toLowerCase();
  if (!qLower) return mapped.slice(0, lim);

  return mapped
    .filter((r) => {
      const blob = `${r.candidate_name} ${r.candidate_email} ${r.job_title} ${r.status}`.toLowerCase();
      return blob.includes(qLower);
    })
    .slice(0, lim);
}

async function getOpenJobs(orgId: string, limit = 5): Promise<JobResult[]> {
  const { data, error } = await supabase
    .from('ggj_b2b_jobs')
    .select('id, title, status, openings_count, clients:ggj_b2b_clients(name)')
    .eq('org_id', orgId)
    .eq('status', 'open')
    .limit(Math.min(limit, 10));

  if (error) throw error;

  return ((data ?? []) as unknown[]).map((r: unknown) => {
    const row = r as Record<string, unknown>;
    const clients = row.clients as { name: string } | null;
    return {
      id: String(row.id),
      title: String(row.title ?? ''),
      client_name: clients?.name ?? '—',
      status: String(row.status),
      openings_count: Number(row.openings_count ?? 1),
    };
  });
}

async function getPipelineStats(orgId: string): Promise<PipelineStats> {
  // Use the report view for server-side aggregation instead of fetching all rows
  const { data, error } = await supabase
    .from('ggj_b2b_report_pipeline')
    .select('status, total')
    .eq('org_id', orgId);

  if (error) {
    // Fallback to direct count if view is unavailable
    const { data: raw } = await supabase
      .from('ggj_b2b_applications')
      .select('status')
      .eq('org_id', orgId);

    const counts: Record<string, number> = {};
    for (const row of raw ?? []) {
      const s = (row as { status: string }).status;
      counts[s] = (counts[s] ?? 0) + 1;
    }
    const by_status = Object.entries(counts)
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count);
    return { total: raw?.length ?? 0, by_status };
  }

  const by_status = (data ?? []).map((r) => ({
    status: String((r as Record<string, unknown>).status),
    count: Number((r as Record<string, unknown>).total ?? 0),
  }));
  const total = by_status.reduce((sum, s) => sum + s.count, 0);
  return { total, by_status };
}

async function searchMatchResults(
  orgId: string,
  query?: string,
  minScore?: number,
  limit = 5,
): Promise<MatchSearchRow[]> {
  const lim = Math.min(Math.max(1, limit), 10);
  let q = supabase
    .from('ggj_b2b_match_results')
    .select(
      'composite_score, recommendation, matched_skills, candidate:ggj_b2b_candidates(full_name), job:ggj_b2b_jobs(title)',
    )
    .eq('org_id', orgId)
    .order('composite_score', { ascending: false })
    .limit(50);

  if (minScore != null && minScore > 0) {
    q = q.gte('composite_score', minScore);
  }

  const { data, error } = await q;
  if (error) throw error;

  const rows = (data ?? []).map((r: unknown) => {
    const row = r as Record<string, unknown>;
    const candidate = row.candidate as { full_name: string } | null;
    const job = row.job as { title: string } | null;
    return {
      candidate_name: candidate?.full_name ?? '—',
      job_title: job?.title ?? '—',
      composite_score: Number(row.composite_score ?? 0),
      recommendation: String(row.recommendation ?? ''),
      matched_skills: (row.matched_skills as string[] | null) ?? [],
    };
  });

  if (query) {
    const qLower = query.trim().toLowerCase();
    return rows
      .filter((r) => `${r.candidate_name} ${r.job_title}`.toLowerCase().includes(qLower))
      .slice(0, lim);
  }

  return rows.slice(0, lim);
}

async function getInterviewStatus(
  orgId: string,
  statusFilter?: string,
  limit = 5,
): Promise<InterviewStatusRow[]> {
  const lim = Math.min(Math.max(1, limit), 10);
  let q = supabase
    .from('ggj_b2b_ai_interview_sessions')
    .select('id, status, session_type, created_at, completed_at, candidate:ggj_b2b_candidates(full_name)')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false })
    .limit(lim);

  if (statusFilter) {
    q = q.eq('status', statusFilter);
  }

  const { data, error } = await q;
  if (error) throw error;

  return (data ?? []).map((r: unknown) => {
    const row = r as Record<string, unknown>;
    const candidate = row.candidate as { full_name: string } | null;
    return {
      session_id: String(row.id),
      candidate_name: candidate?.full_name ?? '—',
      status: String(row.status),
      session_type: String(row.session_type ?? 'screening'),
      created_at: String(row.created_at),
      completed_at: row.completed_at ? String(row.completed_at) : null,
    };
  });
}

function draftEmail(
  type: string,
  candidateName: string,
  jobTitle: string,
): { subject: string; body: string } {
  const name = candidateName || 'the candidate';
  const job = jobTitle || 'the role';

  const templates: Record<string, { subject: string; body: string }> = {
    interview_invite: {
      subject: `Interview Invitation \u2013 ${job}`,
      body: `Hi ${name},\n\nThank you for your application for the ${job} position. We were impressed with your profile and would like to invite you for an interview.\n\nPlease let us know your availability for a 45-minute call this week or next.\n\nLooking forward to speaking with you.\n\nBest regards,\nThe Recruitment Team`,
    },
    rejection: {
      subject: `Your application for ${job}`,
      body: `Hi ${name},\n\nThank you for taking the time to apply for the ${job} position and for your interest in our organisation.\n\nAfter careful consideration, we have decided to move forward with other candidates whose experience more closely matches our current requirements.\n\nWe will keep your profile on file for future opportunities.\n\nWe wish you all the best in your job search.\n\nKind regards,\nThe Recruitment Team`,
    },
    offer: {
      subject: `Offer of Employment \u2013 ${job}`,
      body: `Hi ${name},\n\nWe are delighted to extend an offer of employment for the ${job} position.\n\nWe will follow up shortly with the formal offer letter detailing compensation, benefits, and the proposed start date.\n\nPlease do not hesitate to reach out if you have any questions in the meantime.\n\nWarm regards,\nThe Recruitment Team`,
    },
    follow_up: {
      subject: `Following up on your application \u2013 ${job}`,
      body: `Hi ${name},\n\nI hope this message finds you well. I wanted to follow up on your application for the ${job} position.\n\nWe are still reviewing applications and expect to be in touch shortly. We appreciate your patience.\n\nBest regards,\nThe Recruitment Team`,
    },
  };

  return templates[type] ?? templates['follow_up'];
}

// ── Dispatcher ──────────────────────────────────────────────────────────────

export async function executeTool(orgId: string, call: ToolCall): Promise<ToolResult> {
  try {
    switch (call.tool) {
      case 'search_candidates': {
        const rows = await searchCandidates(
          orgId,
          String(call.args.query ?? ''),
          Number(call.args.limit ?? 5),
        );
        return { tool: call.tool, args: call.args, data: { kind: 'candidates', rows } };
      }
      case 'search_applications': {
        const rows = await searchApplications(
          orgId,
          String(call.args.query ?? ''),
          call.args.status ? String(call.args.status) : undefined,
          Number(call.args.limit ?? 5),
        );
        return { tool: call.tool, args: call.args, data: { kind: 'applications', rows } };
      }
      case 'get_open_jobs': {
        const rows = await getOpenJobs(orgId, Number(call.args.limit ?? 5));
        return { tool: call.tool, args: call.args, data: { kind: 'jobs', rows } };
      }
      case 'get_pipeline_stats': {
        const stats = await getPipelineStats(orgId);
        return { tool: call.tool, args: call.args, data: { kind: 'pipeline', stats } };
      }
      case 'search_match_results': {
        const rows = await searchMatchResults(
          orgId,
          call.args.query ? String(call.args.query) : undefined,
          call.args.min_score != null ? Number(call.args.min_score) : undefined,
          Number(call.args.limit ?? 5),
        );
        return { tool: call.tool, args: call.args, data: { kind: 'matches', rows } };
      }
      case 'get_interview_status': {
        const rows = await getInterviewStatus(
          orgId,
          call.args.status ? String(call.args.status) : undefined,
          Number(call.args.limit ?? 5),
        );
        return { tool: call.tool, args: call.args, data: { kind: 'interviews', rows } };
      }
      case 'draft_email': {
        const draft = draftEmail(
          String(call.args.type ?? 'follow_up'),
          String(call.args.candidate_name ?? ''),
          String(call.args.job_title ?? ''),
        );
        return { tool: call.tool, args: call.args, data: { kind: 'draft', ...draft } };
      }
      default:
        return { tool: call.tool as AgentToolName, args: call.args, error: 'Unknown tool' };
    }
  } catch (err) {
    return {
      tool: call.tool,
      args: call.args,
      error: err instanceof Error ? err.message : 'Tool execution failed',
    };
  }
}

function coerceNumericToolField(value: unknown): unknown {
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return value;
}

/** Normalise args after JSON parse so `executeTool` always sees numbers where expected. */
function normalizeParsedToolArgs(args: Record<string, unknown>): Record<string, unknown> {
  const out = { ...args };
  if ('limit' in out) out.limit = coerceNumericToolField(out.limit);
  if ('min_score' in out) out.min_score = coerceNumericToolField(out.min_score);
  return out;
}

/**
 * Parse tool calls from the LLM response.
 * Supports both OpenAI function calling format (tool_calls array) and
 * legacy JSON-in-text format for backward compatibility.
 */
export function parseToolCall(raw: string): ToolCall | null {
  let trimmed = raw.trim();
  const fence = /^```(?:json)?\s*([\s\S]*?)```/m;
  const m = trimmed.match(fence);
  if (m) trimmed = m[1].trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    const obj = JSON.parse(trimmed) as { tool?: unknown; args?: unknown };
    if (typeof obj.tool === 'string' && typeof obj.args === 'object' && obj.args !== null) {
      return {
        tool: obj.tool as AgentToolName,
        args: normalizeParsedToolArgs(obj.args as Record<string, unknown>),
      };
    }
  } catch {
    // not JSON
  }
  return null;
}

/**
 * Parse OpenAI-format tool_calls from the LLM response object.
 */
export function parseOpenAIToolCalls(
  toolCalls: Array<{
    id: string;
    type: string;
    function: { name: string; arguments: string };
  }>,
): ToolCall[] {
  return toolCalls
    .filter((tc) => tc.type === 'function')
    .map((tc) => {
      let args: Record<string, unknown> = {};
      try {
        args = JSON.parse(tc.function.arguments) as Record<string, unknown>;
      } catch {
        // empty
      }
      return {
        tool: tc.function.name as AgentToolName,
        args: normalizeParsedToolArgs(args),
      };
    });
}

/** Compact structured context appended to assistant history for follow-ups. */
function toolContextForFollowUps(result: ToolResult): string | null {
  if (result.error || !result.data) return null;
  const d = result.data;
  if (d.kind === 'pipeline') {
    const byStatus = d.stats.by_status.map((s) => `${s.status}=${s.count}`).join(', ');
    return `Pipeline total ${d.stats.total}. Counts: ${byStatus}.`;
  }
  if (d.kind === 'applications') {
    return `Application search: ${d.rows.length} row(s).`;
  }
  if (d.kind === 'candidates') {
    return `Candidate search: ${d.rows.length} row(s): ${d.rows.map((c) => c.full_name).join(', ')}.`;
  }
  if (d.kind === 'jobs') {
    return `Open jobs: ${d.rows.length}: ${d.rows.map((j) => j.title).join('; ')}.`;
  }
  if (d.kind === 'matches') {
    return `Match results: ${d.rows.length} row(s). Top: ${d.rows.slice(0, 3).map((m) => `${m.candidate_name}→${m.job_title}(${m.composite_score})`).join(', ')}.`;
  }
  if (d.kind === 'interviews') {
    return `Interview sessions: ${d.rows.length}: ${d.rows.map((i) => `${i.candidate_name}(${i.status})`).join(', ')}.`;
  }
  if (d.kind === 'draft') {
    return `Email draft subject: "${d.subject}".`;
  }
  return null;
}

export function augmentAssistantHistoryForLlm(narration: string, toolResult: ToolResult): string {
  const ctx = toolContextForFollowUps(toolResult);
  if (!ctx) return narration;
  return `${narration}\n\n[Agent context]\n${ctx}`;
}

export function toolResultSummary(result: ToolResult): string {
  if (result.error) return `Tool error: ${result.error}`;
  const d = result.data;
  if (!d) return 'No data returned.';

  if (d.kind === 'candidates') {
    if (!d.rows.length) return 'No candidates found matching that query.';
    return `Found ${d.rows.length} candidate(s): ${d.rows.map((c) => `${c.full_name}${c.current_title ? ` (${c.current_title})` : ''}`).join(', ')}.`;
  }
  if (d.kind === 'applications') {
    if (!d.rows.length) return 'No applications matched.';
    return `${d.rows.length} application(s): ${d.rows.map((a) => `${a.candidate_name} \u2192 ${a.job_title} (${a.status})`).join('; ')}.`;
  }
  if (d.kind === 'jobs') {
    if (!d.rows.length) return 'No open jobs found.';
    return `${d.rows.length} open job(s): ${d.rows.map((j) => `${j.title} at ${j.client_name}`).join('; ')}.`;
  }
  if (d.kind === 'pipeline') {
    const all = d.stats.by_status.map((s) => `${s.status}: ${s.count}`).join(', ');
    return `Pipeline total ${d.stats.total} applications \u2014 by status: ${all}.`;
  }
  if (d.kind === 'matches') {
    if (!d.rows.length) return 'No match results found.';
    return `${d.rows.length} match(es): ${d.rows.map((m) => `${m.candidate_name} \u2192 ${m.job_title} (score: ${m.composite_score})`).join('; ')}.`;
  }
  if (d.kind === 'interviews') {
    if (!d.rows.length) return 'No interview sessions found.';
    return `${d.rows.length} session(s): ${d.rows.map((i) => `${i.candidate_name} (${i.status})`).join('; ')}.`;
  }
  if (d.kind === 'draft') {
    return `Email draft ready. Subject: "${d.subject}".`;
  }
  return '';
}
